// utils/sseClient.js
import TokenService from '../services/token.service';

/**
 * Cliente SSE (Server-Sent Events) para streaming de respuestas
 * Maneja la conexión con endpoints que devuelven text/event-stream
 */

/**
 * Crear una conexión SSE y procesar eventos
 * @param {string} url - URL del endpoint (relativa a baseURL)
 * @param {Object} options - Opciones de configuración
 * @param {Object} options.body - Cuerpo del request (para POST)
 * @param {Function} options.onContent - Callback para chunks de contenido
 * @param {Function} options.onMetadata - Callback para metadata
 * @param {Function} options.onDone - Callback cuando termina
 * @param {Function} options.onError - Callback para errores
 * @param {AbortSignal} options.signal - Signal para cancelar la request
 * @returns {Promise<void>}
 */
export const streamChat = async (url, options = {}) => {
  const {
    body,
    onContent,
    onMetadata,
    onDone,
    onError,
    signal,
  } = options;

  const baseURL = process.env.REACT_APP_ASSISTANT_API_URL;
  const fullUrl = `${baseURL}${url}`;
  const token = TokenService.getAccessToken();

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      // Manejar errores HTTP
      const errorData = await response.json().catch(() => ({}));
      
      // Extraer mensaje de error de diferentes formatos
      let errorMessage = 'Unknown error';
      if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (errorData.detail?.message) {
        errorMessage = errorData.detail.message;
      } else if (errorData.detail?.error) {
        errorMessage = errorData.detail.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else {
        errorMessage = `HTTP ${response.status}`;
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      
      if (onError) {
        onError(error);
      }
      throw error;
    }

    // Verificar que sea SSE
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('text/event-stream')) {
      // Si no es SSE, probablemente sea JSON (respuesta completa)
      const data = await response.json();
      if (onContent && data.response) {
        onContent(data.response);
      }
      if (onMetadata && data.metadata) {
        onMetadata(data.metadata);
      }
      if (onDone) {
        onDone(data);
      }
      return data;
    }

    // Procesar stream SSE
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('🔴 [SSE PARSER] Stream ended. Remaining buffer:', buffer);
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Guardar línea incompleta

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6); // Remover 'data: '
          
          if (data === '[DONE]') {
            if (onDone) onDone();
            continue;
          }

          try {
            const parsed = JSON.parse(data);
            
            switch (parsed.type) {
              case 'content':
                if (onContent) {
                  let contentToSend = parsed.content;
                  console.log('🔍 [SSE PARSER] Original content:', contentToSend);
                  console.log('🔍 [SSE PARSER] Content type:', typeof contentToSend);
                  
                  // Si el content es un string que parece JSON, intentar parsearlo
                  if (typeof contentToSend === 'string' && contentToSend.trim().startsWith('{')) {
                    console.log('🔍 [SSE PARSER] Content looks like JSON, attempting to parse...');
                    try {
                      // Limpiar saltos de línea literales que rompen el JSON
                      const cleanedContent = contentToSend.replace(/\n/g, ' ').replace(/\r/g, '');
                      const parsedContent = JSON.parse(cleanedContent);
                      console.log('🔍 [SSE PARSER] Parsed content:', parsedContent);
                      
                      // Detectar si es un JSON Schema (tiene properties/type/required)
                      let responseValue = null;
                      if (parsedContent.properties && parsedContent.properties.response) {
                        console.log('🔍 [SSE PARSER] Detected JSON Schema format');
                        responseValue = parsedContent.properties.response;
                      } else if (parsedContent.response !== undefined) {
                        responseValue = parsedContent.response;
                      }
                      
                      console.log('🔍 [SSE PARSER] Has response field:', responseValue !== null);
                      
                      // Si tiene campo 'response', usar solo ese campo
                      if (responseValue !== null) {
                        console.log('🔍 [SSE PARSER] Response value:', responseValue);
                        console.log('🔍 [SSE PARSER] Response type:', typeof responseValue);
                        
                        // Si response es un string que parece JSON, parsearlo también
                        if (typeof responseValue === 'string' && (responseValue.trim().startsWith('[') || responseValue.trim().startsWith('{'))) {
                          try {
                            responseValue = JSON.parse(responseValue);
                            console.log('🔍 [SSE PARSER] Response after parsing:', responseValue);
                          } catch (e) {
                            console.log('⚠️ [SSE PARSER] Response is string but not valid JSON:', e.message);
                          }
                        }
                        
                        console.log('🔍 [SSE PARSER] Response is array:', Array.isArray(responseValue));
                        
                        // Si response es un array de objetos, formatear como lista markdown
                        if (Array.isArray(responseValue)) {
                          console.log('📝 [SSE PARSER] Formatting as markdown list...');
                          const items = responseValue.map((item, idx) => {
                            // Formatear cada item de forma más limpia y espaciada
                            let formattedItem = '';
                            
                            // Si tiene subject (email), usar formato especial
                            if (item.subject) {
                              formattedItem = `### 📧 ${item.subject}\n\n`;
                              if (item.from) formattedItem += `**De:** ${item.from}\n\n`;
                              if (item.date) formattedItem += `**Fecha:** ${item.date}\n\n`;
                              if (item.snippet) {
                                const snippet = item.snippet.length > 200 ? item.snippet.substring(0, 200) + '...' : item.snippet;
                                formattedItem += `> ${snippet}`;
                              }
                            } else {
                              // Formato genérico para otros tipos de objetos
                              formattedItem = `**${idx + 1}.**\n\n`;
                              formattedItem += Object.entries(item)
                                .map(([key, value]) => `**${key}:** ${value}`)
                                .join('\n\n');
                            }
                            
                            return formattedItem;
                          }).join('\n\n---\n\n');
                          contentToSend = items;
                          console.log('✅ [SSE PARSER] Formatted content:', contentToSend);
                        } else if (typeof responseValue === 'object') {
                          console.log('📝 [SSE PARSER] Formatting as property list...');
                          // Si es un objeto simple, formatear como lista de propiedades
                          contentToSend = Object.entries(responseValue)
                            .map(([key, value]) => `**${key}:** ${value}`)
                            .join('\n\n');
                          console.log('✅ [SSE PARSER] Formatted content:', contentToSend);
                        } else if (typeof responseValue === 'string') {
                          console.log('📝 [SSE PARSER] Using response string, preserving line breaks');
                          // Preservar saltos de línea en texto plano
                          contentToSend = responseValue;
                        } else {
                          console.log('📝 [SSE PARSER] Using response value as-is');
                          contentToSend = responseValue;
                        }
                      }
                    } catch (e) {
                      console.log('❌ [SSE PARSER] Failed to parse JSON:', e.message);
                      // Si falla el parse, usar el content original
                    }
                  }
                  
                  console.log('📤 [SSE PARSER] Final content to send:', contentToSend);
                  onContent(contentToSend);
                }
                break;
              case 'metadata':
                console.log('🟣 [SSE PARSER] metadata event received, display_blocks:', parsed.metadata?.display_blocks);
                if (onMetadata) {
                  onMetadata(parsed.metadata);
                }
                break;
              case 'done':
                if (onDone) {
                  onDone(parsed.data || parsed);
                }
                break;
              case 'error':
                if (onError) {
                  onError(new Error(parsed.error || 'Error en streaming'));
                }
                break;
              default:
                console.warn('Tipo de evento SSE desconocido:', parsed.type);
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError, data);
          }
        }
      }
    }

    // Procesar cualquier dato restante en el buffer
    if (buffer.startsWith('data: ')) {
      try {
        const data = buffer.slice(6);
        if (data && data !== '[DONE]') {
          const parsed = JSON.parse(data);
          if (parsed.type === 'done' && onDone) {
            onDone(parsed.data || parsed);
          }
        }
      } catch (e) {
        // Ignorar errores de parsing del buffer final
      }
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Stream cancelado');
      return;
    }
    
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};

/**
 * Helper para crear un AbortController con timeout
 * @param {number} timeoutMs - Timeout en milisegundos
 * @returns {AbortController}
 */
export const createStreamController = (timeoutMs = 120000) => {
  const controller = new AbortController();
  
  if (timeoutMs > 0) {
    setTimeout(() => {
      controller.abort();
    }, timeoutMs);
  }
  
  return controller;
};

export default streamChat;
